/* eslint-disable react/jsx-closing-tag-location */
import * as React from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@frontend/shadcn/components/ui/tabs';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@frontend/shadcn/components/ui/accordion';
import { Checkbox } from '@frontend/shadcn/components/ui/checkbox';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@frontend/shadcn/components/ui/tooltip';
import { Info } from 'lucide-react';
import {
  BOOST_TYPE,
  BoostInput,
  OFFER_PRICE_RULE_TYPE,
  OFFER_PROMO_USAGE_LIMIT_RULE,
  OFFER_SOURCE,
  OFFER_STATUS,
} from '@frontend/applications/AffiliatesApp/types/globalTypes';
import { Input } from '@frontend/shadcn/components/ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@frontend/shadcn/components/ui/select';
import { isEmpty, isNil, isNull, values } from 'lodash';
import { Calendar } from '@frontend/shadcn/components/ui/calendar';
import { Button } from '@frontend/shadcn/components/ui/button';
import { Popover, PopoverTrigger } from '@frontend/shadcn/components/ui/popover';
import { PopoverContent } from '@radix-ui/react-popover';
import { CalendarIcon } from '@revfluence/fresh-icons/regular/esm';
import { CircleCheckIcon, GiftIcon, SackDollarIcon } from '@revfluence/fresh-icons/solid/esm';
import { format } from 'date-fns';
import moment from 'moment';
import { message } from '@affiliates/AspireUI';
import { getEmptyBoostInput, transformDiscountBoostInputToPromoCodeValuesForBoost, transformDiscountToUpdatePromoInput } from '@frontend/applications/AffiliatesApp/utils';
import { RefreshOfferDefaultImage } from '@frontend/applications/AffiliatesApp/components/OfferImage/RefreshOfferDefaultImage';
import { ProductsProvider } from '@frontend/app/containers/Settings/ProductFulfillment/Products/ProductsContext';
import { faTags } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TOfferMember } from '@frontend/applications/AffiliatesApp/components/MemberTable/types';
import { useClientFeatures } from '@frontend/context/ClientFeatureContext';
import { useOfferDetailsContext } from '@frontend/context/OfferDetailsContext';
import { useAppContext } from '@frontend/context/AppContext';
import { useGetClientConfig } from '@frontend/applications/ProductFulfillmentApp/hooks/useGetClientConfig';
import { Source } from '@frontend/applications/ProductFulfillmentApp/types/globalTypes';
import {
  IShopifyPromoCodeFormOptions,
  IShopifyPromoCodeFormValues,
  PrefixTypeStrategy,
  PROMO_OFFER_TYPES,
  PurchaseType,
  RecurringCycleLimit,
  TDisabledMap,
  TFormValues,
} from '../../../types';
import { DiscountBoost } from './DiscountBoost';
import { BoostEligibility } from './BoostEligibility';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from '@/shadcn/components/ui/sheet';
import { Label } from '@/shadcn/components/ui/label';
import { FreeProductShopifyBoost } from './FreeProductShopifyBoost';

export interface BoostSheetProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  mode?: 'CREATE' | 'EDIT';
  boosts: BoostInput[];
  setBoosts: (boosts: BoostInput[] | ((boosts: BoostInput[]) => BoostInput[])) => void;
  boostInFocus: BoostInput;
  setBoostInFocus: (boost: BoostInput | ((input: BoostInput) => BoostInput)) => void;
  formOptionsData?: IShopifyPromoCodeFormOptions;
  isSubscriptionEnable: boolean;
  disabledShopify: TDisabledMap<IShopifyPromoCodeFormValues>;
  setIndexInFocus?: (index: number | null) => void;
  indexInFocus?: number | null;
  offer: TFormValues;
  offerMembers: TOfferMember[];
}

export const BOOST_CONVERSION_CONDITIONS = {
  ON_X_CONVERSION: 'ON_X_CONVERSION', // one time
  ON_EVERY_X_CONVERSION: 'ON_EVERY_X_CONVERSION', // repeated
  ON_FIRST_CONVERSION: 'ON_FIRST_CONVERSION', // one time
};

export const BOOST_SALES_CONDITIONS = {
  ON_X_TOTAL_SALES: 'ON_X_TOTAL_SALES', // one time
  ON_EVERY_X_TOTAL_SALES: 'ON_EVERY_X_TOTAL_SALES', // repeated
  ON_FIRST_X_ORDER_VALUE: 'ON_FIRST_X_ORDER_VALUE', // one time
  ON_EVERY_X_ORDER_VALUE: 'ON_EVERY_X_ORDER_VALUE', // repeated
};

export const BOOST_CONDITIONS_OPTIONS: { value: string; label: string }[] = [
  { value: BOOST_CONVERSION_CONDITIONS.ON_X_CONVERSION, label: 'When number of conversions = X' },
  { value: BOOST_CONVERSION_CONDITIONS.ON_EVERY_X_CONVERSION, label: 'For every X conversions' },
  { value: BOOST_CONVERSION_CONDITIONS.ON_FIRST_CONVERSION, label: 'On first conversion' },
  { value: BOOST_SALES_CONDITIONS.ON_X_TOTAL_SALES, label: 'When total sales >= $X' },
  { value: BOOST_SALES_CONDITIONS.ON_EVERY_X_TOTAL_SALES, label: 'For every $X in total sales' },
  { value: BOOST_SALES_CONDITIONS.ON_FIRST_X_ORDER_VALUE, label: 'On first order value >= $X' },
  { value: BOOST_SALES_CONDITIONS.ON_EVERY_X_ORDER_VALUE, label: 'When a single order subtotal >= $X' },
];

export const BoostDrawer = (props: BoostSheetProps): JSX.Element => {
  const {
    open,
    setOpen,
    mode = 'CREATE',
    boostInFocus,
    setBoostInFocus,
    setBoosts,
    boosts,
    formOptionsData,
    isSubscriptionEnable,
    disabledShopify,
    setIndexInFocus,
    indexInFocus,
    offer,
    offerMembers,
  } = props;
  const { isShopifyConnected } = useOfferDetailsContext();
  const [tab, setTab] = React.useState(
    !isShopifyConnected ? BOOST_TYPE.CASHBACK_BOOST : BOOST_TYPE.DISCOUNT_BOOST
  );
  const [draftBoost, setDraftBoost] = React.useState<BoostInput | null>(null);
  const [maxLimitChecked, setMaxLimitChecked] = React.useState(false);
  const { pfaApolloClient } = useAppContext();
  const [endDateError, setEndDateError] = React.useState<string | null>(null);
  const [discountEndDateError, setDiscountEndDateError] = React.useState<string | null>(null);
  const { pfaBoost: isPfaBoostEnabled, advancedSelectionEligibility: isAdvancedSelectionEnabled, cashbackPayments: isCashbackEnabled } = useClientFeatures();
  const [isDateSelected, setIsDateSelected] = React.useState(false);
  const [discountBoostInput, setDiscountBoostInput] = React.useState<Partial<IShopifyPromoCodeFormValues> | null>(null);
  const { clientConfig, loading: isClientConfigLoading } = useGetClientConfig({ client: pfaApolloClient });
  const isNonShopifyBoostEnabled = !isClientConfigLoading && clientConfig?.importSyncSettings?.importSource === Source.CSV;
  const isShowDiscountBoost = isShopifyConnected && !isNonShopifyBoostEnabled;

  React.useEffect(() => {
    setMaxLimitChecked(!!boostInFocus?.maxLimit);
  }, [boostInFocus]);
  React.useEffect(() => {
    if (draftBoost?.activeStartDate || draftBoost?.activeEndDate) {
      setIsDateSelected(true);
    } else {
      setIsDateSelected(false);
    }
  }, [draftBoost?.activeStartDate, draftBoost?.activeEndDate]);
  const isDraftBoostValid = React.useMemo(() => {
    if (!draftBoost) return false;
    if (!validateDates(draftBoost?.activeStartDate ?? null, draftBoost?.activeEndDate ?? null)) return false;
    if (isEmpty(draftBoost.label) || isNull(draftBoost.triggerType) || isNull(draftBoost.triggerValue)) return false;
    if (!values({ ...BOOST_CONVERSION_CONDITIONS, ...BOOST_SALES_CONDITIONS }).includes(draftBoost.triggerType)) return false;
    if (maxLimitChecked && !draftBoost.maxLimit) return false;

    // Validate cashback boost
    if (draftBoost.boostType === BOOST_TYPE.CASHBACK_BOOST) {
      if (!draftBoost.cashbackAmount || draftBoost.cashbackAmount <= 0) return false;
    }

    return true;
  }, [draftBoost, maxLimitChecked]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    if (boostInFocus?.boostType === BOOST_TYPE.DISCOUNT_BOOST) {
      if (mode === 'CREATE' && !discountBoostInput) {
        setDiscountBoostInput({
          codeSuffix: '',
          description: '',
          flatPayout: null,
          groupName: '',
          imageUrl: null,
          icon: null,
          name: '',
          payoutType: null,
          payoutOptions: null,
          percentPayout: null,
          prefixType: PrefixTypeStrategy.FIRST_INITIAL_LAST_NAME_ONLY,
          priceRuleAmount: '10',
          priceRuleType: OFFER_PRICE_RULE_TYPE.PERCENTAGE,
          source: OFFER_SOURCE.SHOPIFY,
          specialLimitNewCustomersOnly: false,
          specialLimitOnePerSale: true,
          specialLimitUsageCapAmount: '1',
          specialLimitUsageCapEnabled: true,
          usageLimitAmount: null,
          usageLimitRule: OFFER_PROMO_USAGE_LIMIT_RULE.NONE,
          productCollections: [],
          status: OFFER_STATUS.ACTIVE,
          activeTime: null,
          activeDate: moment(),
          endDate: null,
          endTime: null,
          recurringCycleLimitAmount: 0,
          offerCodePurchaseRestrictionsRule: RecurringCycleLimit.LIMIT_DISCOUNT_TO_THE_FIRST_PAYMENT,
          isEndDateEnable: false,
          isNewFlow: true,
          purchaseType: PurchaseType.ONE_TIME,
          isMultipleShopifySyncEnabled: false,
          isSameDiscountMultipleShopify: true,
          multiShopifyEnabled: false,
          clientsForSync: [],
          connectedAdvertiserForSync: [],
          allClientForSync: [],
          isUngrouped: false,
          lockEditing: [],
          productDiscounts: false,
          shippingDiscounts: false,
          orderDiscounts: false,
          isPromoLink: false,
          isLandingPageEnabled: false,
          landingPageUrl: null,
          shopifyRedirectUrl: null,
          isSecureCodes: false,
          linkedShopifyOfferId: '',
          isReadOnly: false,
          offerType: PROMO_OFFER_TYPES.AMOUNT_OFF,
        });
      } else {
        if (boostInFocus.discountBoostInput) {
          const promoCodeFormValues = transformDiscountBoostInputToPromoCodeValuesForBoost(boostInFocus?.discountBoostInput);
          setDiscountBoostInput(promoCodeFormValues);
        }
      }
    }
    if (boostInFocus?.boostType !== BOOST_TYPE.DISCOUNT_BOOST) {
      setDiscountBoostInput(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boostInFocus?.boostType, boostInFocus, mode]);

  /* eslint-disable react-hooks/exhaustive-deps */
  const handleClose = React.useCallback(() => {
    setOpen(false);
    setDraftBoost(null);
    setDiscountBoostInput(null);
    // Reset boostInFocus to appropriate default based on Shopify connection
    const defaultBoostType = !isShopifyConnected ? BOOST_TYPE.CASHBACK_BOOST : BOOST_TYPE.DISCOUNT_BOOST;
    setBoostInFocus(getEmptyBoostInput(defaultBoostType));
    setIndexInFocus(null);
  }, [isShopifyConnected]);

  function validateDates(start: Date | null, end: Date | null) {
    if (start && end && end <= start) {
      setEndDateError('End date must be after start date.');
      return false;
    }
    setEndDateError(null);
    return true;
  }

  // Validate Discount Code dates vs Reward dates
  function validateDiscountCodeVsRewardDates(): string | null {
    if (!draftBoost?.discountBoostInput) return null;

    const discountStartDate = draftBoost.discountBoostInput.startDate;
    const discountEndDate = draftBoost.discountBoostInput.endDate;
    const rewardStartDate = draftBoost.activeStartDate;
    const rewardEndDate = draftBoost.activeEndDate;

    // Convert to Date objects and normalize to start of day for comparison
    const discountStart = discountStartDate ? new Date(new Date(discountStartDate).setHours(0, 0, 0, 0)) : null;
    const discountEnd = discountEndDate ? new Date(new Date(discountEndDate).setHours(0, 0, 0, 0)) : null;
    const rewardStart = rewardStartDate ? new Date(new Date(rewardStartDate).setHours(0, 0, 0, 0)) : null;
    const rewardEnd = rewardEndDate ? new Date(new Date(rewardEndDate).setHours(0, 0, 0, 0)) : null;

    // ERROR: Reward starts before discount code is active
    if (rewardStart && discountStart && rewardStart < discountStart) {
      return 'Reward starts before the discount code is active. Creators can only redeem the code once it becomes active.';
    }

    // ERROR: Reward starts after discount code expires
    if (rewardStart && discountEnd && rewardStart > discountEnd) {
      return 'Reward starts after the discount code expires. Please adjust the dates.';
    }

    // ERROR: Reward ends after discount code expires
    if (rewardEnd && discountEnd && rewardEnd > discountEnd) {
      return 'Reward ends after the discount code expires. Please adjust the dates.';
    }

    // ERROR: Infinite reward but limited discount code
    if (!rewardEnd && discountEnd) {
      return 'Reward has no end date but discount code expires. Please set a reward end date or remove the discount code end date.';
    }

    return null; // Valid - no errors
  }
  React.useEffect(() => {
    if (discountBoostInput) {
      const updatePromoInput = transformDiscountToUpdatePromoInput(
        discountBoostInput,
        formOptionsData,
        isSubscriptionEnable,
      );
      setDraftBoost((prev) => ({ ...prev, discountBoostInput: updatePromoInput }));
    } else {
      setDraftBoost((prev) => ({ ...prev, discountBoostInput: null }));
    }
  }, [discountBoostInput, formOptionsData, isSubscriptionEnable]);
  // Update validation on date change
  React.useEffect(() => {
    validateDates(draftBoost?.activeStartDate ?? null, draftBoost?.activeEndDate ?? null);
  }, [draftBoost?.activeStartDate, draftBoost?.activeEndDate]);

  const onDrawerSubmit = React.useCallback(() => {
    if (!draftBoost) return;
    if (!validateDates(draftBoost?.activeStartDate ?? null, draftBoost?.activeEndDate ?? null)) return;

    // Validate Discount Code vs Reward dates
    const dateValidationError = validateDiscountCodeVsRewardDates();
    if (dateValidationError) {
      message.destroy();
      message.warning(dateValidationError);
    }

    if (mode === 'CREATE') { /** CREATE* */
      setBoosts([...boosts, { ...draftBoost, action: 'CREATE' }]);
      handleClose();
    } else { /** EDIT* */
      const updatedBoosts = boosts.map((b, idx) => (idx === indexInFocus ? { ...draftBoost, action: 'UPDATE' } : b));
      setBoosts(updatedBoosts);
      setBoostInFocus({ ...draftBoost });
      handleClose();
    }
  }, [mode, draftBoost, setBoosts, boosts, handleClose, indexInFocus, setBoostInFocus]);

  React.useEffect(() => {
    if (open) {
      setDraftBoost({ ...boostInFocus });
      // Set tab based on the boost type when opening in edit mode
      if (boostInFocus?.boostType) {
        setTab(boostInFocus.boostType);
      } else if (!isShopifyConnected) {
        // Default to cashback boost when Shopify is not connected
        setTab(BOOST_TYPE.CASHBACK_BOOST);
        setDraftBoost((prev) => ({ ...prev, boostType: BOOST_TYPE.CASHBACK_BOOST }));
      }
    }
  }, [open, boostInFocus, isShopifyConnected]);

  function updateDraftBoost<K extends keyof BoostInput>(key: K, value: BoostInput[K]) {
    setDraftBoost((prev) => ({ ...prev, [key]: value }));
  }
  return (
    <Sheet open={open} onOpenChange={() => handleClose()}>
      <SheetContent side="right" className="flex flex-col h-full bg-grey-0 p-0 gap-0 w-[576px] !max-w-[576px]">
        <SheetHeader className="p-4 flex-shrink-0">
          <SheetTitle className="m-0 flex items-center gap-2">
            <RefreshOfferDefaultImage
              imageUrl={offer.imageUrl}
              source={offer.source}
              icon={offer.icon}
              iconClassName="h-4 w-4"
              className="w-9 h-9"
            />
            {mode === 'CREATE' ? 'Create Reward' : 'Edit Reward'}
          </SheetTitle>
        </SheetHeader>
        <div className="border-t flex-shrink-0" />
        <div className="flex-1 overflow-y-auto p-4">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label className="text-primary">Name your reward</Label>
              <Input
                id="reward-name"
                placeholder="Name your reward"
                value={draftBoost?.label || ''}
                onChange={(e) => updateDraftBoost('label', e.target.value)}
                className=""
              />
            </div>
            <div className="flex gap-4">
              <div className="flex-1 flex flex-col gap-2 relative overflow-visible">
                <Label className="text-primary">When</Label>

                <Select
                  value={draftBoost?.triggerType}
                  onValueChange={(val) => {
                    if (val === BOOST_CONVERSION_CONDITIONS.ON_FIRST_CONVERSION) {
                      updateDraftBoost('triggerValue', 0);
                    }
                    if (val === BOOST_SALES_CONDITIONS.ON_FIRST_X_ORDER_VALUE || val === BOOST_CONVERSION_CONDITIONS.ON_FIRST_CONVERSION) {
                      updateDraftBoost('maxLimit', 1);
                      setMaxLimitChecked(true);
                    }
                    updateDraftBoost('triggerType', val);
                  }}
                >
                  <SelectTrigger id="when-select">
                    <SelectValue placeholder="Select trigger" />
                  </SelectTrigger>
                  <SelectContent className="z-[9999]">
                    {BOOST_CONDITIONS_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-32 flex flex-col gap-2">
                <Label className="text-primary">Value</Label>
                <Input
                  id="number-input"
                  type="number"
                  placeholder="e.g. 10"
                  value={draftBoost?.triggerValue ?? ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Only allow positive numbers or empty string
                    if (value === '' || (value.match(/^\d+$/) && parseInt(value, 10) > 0)) {
                      updateDraftBoost('triggerValue', value === '' ? null : parseInt(value, 10));
                    }
                  }}
                  onKeyDown={(e) => {
                    // Prevent negative sign, decimal point, and other non-numeric characters
                    if (['-', '.', 'e', 'E'].includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  min={1}
                  disabled={draftBoost?.triggerType === BOOST_CONVERSION_CONDITIONS.ON_FIRST_CONVERSION}
                />
              </div>
            </div>
            <div>
              <Label className="text-primary">Give creators</Label>
              <Tabs
                value={tab}
                onValueChange={(val) => {
                  setTab(val as BOOST_TYPE);
                  updateDraftBoost('boostType', val as BOOST_TYPE);

                  // Implement mutual exclusion logic
                  if (val === BOOST_TYPE.CASHBACK_BOOST) {
                    // Clear other boost inputs when cashback is selected
                    updateDraftBoost('discountBoostInput', null);
                    updateDraftBoost('boostMetadata', null);
                  } else {
                    // Clear cashback amount when other types are selected
                    updateDraftBoost('cashbackAmount', null);
                  }
                }}
                className="w-full"
              >
                <TabsList className="flex gap-2 justify-start p-0 mt-2 bg-grey-0">
                  {isShowDiscountBoost &&
                    <TabsTrigger
                      value={BOOST_TYPE.DISCOUNT_BOOST}
                      disabled={!isNil(draftBoost?.id) || !isShopifyConnected}
                      className={`h-10 w-max border border-grey-2 rounded-xl !shadow-none relative ${tab === BOOST_TYPE.DISCOUNT_BOOST ? 'border-primary' : ''
                        }`}
                    >
                      <FontAwesomeIcon
                        icon={faTags}
                        className="h-4 w-4 text-blue-4 mr-2"
                      />Discount Code
                      {tab === BOOST_TYPE.DISCOUNT_BOOST && (
                        <CircleCheckIcon className="bg-primary-foreground text-primary absolute top-[-5px] right-[-3px]" />
                      )}
                    </TabsTrigger>}
                  {isPfaBoostEnabled && (
                    <TabsTrigger
                      value={BOOST_TYPE.PFA_BOOST}
                      disabled={!isNil(draftBoost?.id)}
                      className={`h-10 w-max border border-grey-2 rounded-xl !shadow-none relative ${tab === BOOST_TYPE.PFA_BOOST ? 'border-primary' : ''
                        }`}
                    >
                      <GiftIcon className="h-4 w-4 text-magenta-4 mr-2" />
                      Free Product
                      {tab === BOOST_TYPE.PFA_BOOST && (
                        <CircleCheckIcon className="bg-primary-foreground text-primary absolute top-[-5px] right-[-3px]" />
                      )}
                    </TabsTrigger>
                  )}
                  {isCashbackEnabled && (<TabsTrigger
                    value={BOOST_TYPE.CASHBACK_BOOST}
                    disabled={!isNil(draftBoost?.id)}
                    className={`h-10 w-max border border-grey-2 rounded-xl !shadow-none relative ${tab === BOOST_TYPE.CASHBACK_BOOST ? 'border-primary' : ''
                      }`}
                  >
                    <SackDollarIcon className="h-4 w-4 text-teal-4 mr-2" />
                    Cash Reward
                    {tab === BOOST_TYPE.CASHBACK_BOOST &&
                    (
                      <CircleCheckIcon className="bg-primary-foreground text-primary absolute top-[-5px] right-[-3px]" />
                    )}
                  </TabsTrigger>
                  )}
                </TabsList>
                {isShowDiscountBoost && <TabsContent value={BOOST_TYPE.DISCOUNT_BOOST}>
                  {discountBoostInput && (
                    <DiscountBoost
                      discountBoostInput={discountBoostInput}
                      onChange={(discountBoostInput) => setDiscountBoostInput(discountBoostInput)}
                      formOptionsData={formOptionsData}
                      isSubscriptionEnable={isSubscriptionEnable}
                      disabledShopify={disabledShopify}
                      setEndDateError={setDiscountEndDateError}
                      endDateError={discountEndDateError}
                    />
                  )}
                </TabsContent>}
                {isPfaBoostEnabled && (
                  <TabsContent className="bg-gray-50 p-4" value={BOOST_TYPE.PFA_BOOST}>
                    <ProductsProvider>
                      <FreeProductShopifyBoost
                        {...props}
                        draftBoost={draftBoost}
                        updateDraftBoost={(key, value) => updateDraftBoost(key, value)}
                      />
                    </ProductsProvider>
                  </TabsContent>
                )}
                <TabsContent value={BOOST_TYPE.CASHBACK_BOOST}>
                  <div className="mt-4 bg-grey-1">
                    <div className="flex flex-col gap-2">
                      <Label className="text-primary">Cash Reward Value</Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground">$</span>
                        <Input
                          type="number"
                          placeholder="20"
                          value={draftBoost?.cashbackAmount ?? ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            updateDraftBoost('cashbackAmount', value === '' ? null : Number(value));
                          }}
                          className="pl-7"
                          min={0}
                          step={1}
                        />
                      </div>
                      <p className="text-sm text-muted-foreground italic">
                        Note: Cash rewards will be added to the payouts of members.
                      </p>
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            </div>
            <div>
              <Accordion type="single" collapsible className="w-full">
                <AccordionItem value="boost-settings">
                  <AccordionTrigger className=" text-base py-0">Rewards Settings</AccordionTrigger>
                  <AccordionContent>
                    <div className="flex flex-col gap-4 py-2">
                      <TooltipProvider>
                        <Label className="flex items-center gap-2 cursor-pointer text-primary">
                          <Checkbox
                            checked={maxLimitChecked}
                            onCheckedChange={(checked: boolean) => {
                              if (checked) {
                                setMaxLimitChecked(true);
                              } else {
                                setMaxLimitChecked(false);
                                updateDraftBoost('maxLimit', null);
                              }
                            }}
                            id="limit-times"
                            disabled={draftBoost?.triggerType === BOOST_CONVERSION_CONDITIONS.ON_FIRST_CONVERSION || draftBoost?.triggerType === BOOST_SALES_CONDITIONS.ON_FIRST_X_ORDER_VALUE}
                          />
                          <span>Limit number of times this reward can be earned</span>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span tabIndex={0}>
                                <Info className="w-4 h-4 text-muted-foreground" />
                              </span>
                            </TooltipTrigger>
                            <TooltipContent side="right">
                              Set a maximum number of times a creator can earn this reward.
                            </TooltipContent>
                          </Tooltip>
                        </Label>
                        {maxLimitChecked ? (
                          <div className="pl-8 py-2">
                            <Input
                              type="number"
                              min={1}
                              value={draftBoost?.maxLimit ?? ''}
                              onChange={(e) => {
                                const value = e.target.value;
                                updateDraftBoost('maxLimit', value === '' ? null : Number(value));
                              }}
                              placeholder="Enter limit"
                              className="w-32"
                              disabled={draftBoost?.triggerType === BOOST_CONVERSION_CONDITIONS.ON_FIRST_CONVERSION || draftBoost?.triggerType === BOOST_SALES_CONDITIONS.ON_FIRST_X_ORDER_VALUE}
                            />
                          </div>
                        ) : null}
                        <Label className="flex items-center gap-2 cursor-pointer text-primary">
                          <Checkbox
                            checked={!!draftBoost?.requireApproval}
                            onCheckedChange={(checked) => {
                              updateDraftBoost('requireApproval', !!checked);
                              if (checked) {
                                updateDraftBoost('hideInCreatorPortal', true);
                              }
                            }}
                            id="requires-approval"
                          />
                          <span>This reward requires approval first</span>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span tabIndex={0}>
                                <Info className="w-4 h-4 text-muted-foreground" />
                              </span>
                            </TooltipTrigger>
                            <TooltipContent side="right">
                              Manually approve each reward before it's issued to the creator. Enabling this also hides the
                              reward from the member's portal.
                            </TooltipContent>
                          </Tooltip>
                        </Label>
                        <Label className="flex items-center gap-2 cursor-pointer text-primary">
                          <Checkbox
                            checked={!!draftBoost?.hideInCreatorPortal}
                            onCheckedChange={(checked) => updateDraftBoost('hideInCreatorPortal', !!checked)}
                            id="hide-from-portal"
                            disabled={!!draftBoost?.requireApproval}
                          />
                          <span>Hide reward from member's portal.</span>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span tabIndex={0}>
                                <Info className="w-4 h-4 text-muted-foreground" />
                              </span>
                            </TooltipTrigger>
                            <TooltipContent side="right">
                              Creators won't see this reward on their portal. This is useful when you want to offer surprise
                              rewards.
                            </TooltipContent>
                          </Tooltip>
                        </Label>
                        <Label className="flex items-center gap-2 cursor-pointer text-primary">
                          <Checkbox
                            checked={isDateSelected}
                            onCheckedChange={(checked) => {
                              setIsDateSelected(!!checked);
                              if (!checked) {
                                updateDraftBoost('activeStartDate', null);
                                updateDraftBoost('activeEndDate', null);
                              }
                            }}
                            id="active-dates"
                          />
                          <span>Reward active dates</span>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span tabIndex={0}>
                                <Info className="w-4 h-4 text-muted-foreground" />
                              </span>
                            </TooltipTrigger>
                            <TooltipContent side="right">
                              Limit when this reward is active. Outside these dates, it won't trigger.
                            </TooltipContent>
                          </Tooltip>
                        </Label>
                        {isDateSelected && (
                          <div className="pl-1 py-2 flex flex-col gap-2">
                            <div className="flex gap-4">
                              <div>
                                <Popover>
                                  <PopoverTrigger asChild>
                                    <Button variant="outline" className="w-[240px] pl-3 text-left font-normal">
                                      <span className="block text-sm mb-1">
                                        {draftBoost?.activeStartDate
                                          ? format(new Date(draftBoost?.activeStartDate), 'MM/dd/yyyy')
                                          : 'Start Date'}
                                      </span>
                                      <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                    </Button>
                                  </PopoverTrigger>
                                  <PopoverContent className="bg-grey-0 w-auto p-0 pointer-events-auto user-select-text" align="start">
                                    <Calendar
                                      mode="single"
                                      selected={
                                        draftBoost?.activeStartDate ? new Date(draftBoost?.activeStartDate) : undefined
                                      }
                                      onSelect={(date) => updateDraftBoost('activeStartDate', date || null)}
                                    />
                                  </PopoverContent>
                                </Popover>
                              </div>
                              <div>
                                <Popover>
                                  <PopoverTrigger asChild>
                                    <Button variant="outline" className="w-[240px] pl-3 text-left font-normal">
                                      <span className="block text-sm mb-1">
                                        {draftBoost?.activeEndDate
                                          ? format(new Date(draftBoost?.activeEndDate), 'MM/dd/yyyy')
                                          : 'End Date'}
                                      </span>
                                      <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                    </Button>
                                  </PopoverTrigger>
                                  <PopoverContent className="bg-grey-0 w-auto p-0 pointer-events-auto user-select-text" align="start">
                                    <Calendar
                                      mode="single"
                                      selected={draftBoost?.activeEndDate ? new Date(draftBoost?.activeEndDate) : undefined}
                                      onSelect={(date) => updateDraftBoost('activeEndDate', date || null)}
                                    />
                                  </PopoverContent>
                                </Popover>
                                {endDateError && <span className="text-red-500 text-xs mt-1 block">{endDateError}</span>}
                              </div>
                            </div>
                          </div>
                        )}
                      </TooltipProvider>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>

            </div>
            <div>
              <Accordion type="single" collapsible className="w-full">
                <AccordionItem value="boost-eligibility">
                  <AccordionTrigger className="text-base py-0">Rewards Eligibility</AccordionTrigger>
                  <AccordionContent>
                    <div className="flex flex-col gap-4 p-3 border border-grey-2 rounded-lg">
                      <BoostEligibility
                        boostEligibility={draftBoost?.boostEligibility || null}
                        onChange={(boostEligibility) => updateDraftBoost('boostEligibility', boostEligibility)}
                        offerMembers={offerMembers}
                        isAdvancedSelectionEnabled={isAdvancedSelectionEnabled}
                      />
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>

            </div>
          </div>
        </div>
        <SheetFooter className="justify-start sm:justify-start p-4 flex-shrink-0 border-t bg-grey-0">
          <Button
            type="button"
            variant="outline"
            className="px-8 py-3 rounded-xl font-medium bg-white text-foreground shadow-sm"
            onClick={onDrawerSubmit}
            disabled={!isNil(endDateError) || !isNil(discountEndDateError) || !draftBoost || !isDraftBoostValid}
          >
            Done
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};
